<script>
const MINUTE_IN_MILLI_SECONDS = 60000;
const HOUR_IN_MILLI_SECONDS = MINUTE_IN_MILLI_SECONDS * 60;
const DAY_IN_MILLI_SECONDS = HOUR_IN_MILLI_SECONDS * 24;

import {
  dynamicTime,
  dateFormat,
  shortTimestamp,
} from 'shared/helpers/timeHelper';

const normalizeTimestamp = value => {
  if (!value) return null;
  if (typeof value === 'number') {
    return value > 1000000000000 ? Math.floor(value / 1000) : value;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number(value) || null : Math.floor(parsed / 1000);
};

const safeDynamicTime = value => {
  const timestamp = normalizeTimestamp(value);
  return timestamp ? dynamicTime(timestamp) : '';
};

export default {
  name: 'TimeAgo',
  props: {
    isAutoRefreshEnabled: {
      type: Boolean,
      default: true,
    },
    lastActivityTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    createdAtTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    waitingSinceTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    conversationId: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      lastActivityAtTimeAgo: safeDynamicTime(this.lastActivityTimestamp),
      createdAtTimeAgo: safeDynamicTime(this.createdAtTimestamp),
      waitingSinceTimeAgo: safeDynamicTime(this.waitingSinceTimestamp),
      timer: null,
    };
  },
  computed: {
    lastActivityUnixTimestamp() {
      return normalizeTimestamp(this.lastActivityTimestamp);
    },
    createdAtUnixTimestamp() {
      return normalizeTimestamp(this.createdAtTimestamp);
    },
    waitingSinceUnixTimestamp() {
      return normalizeTimestamp(this.waitingSinceTimestamp);
    },
    lastActivityTimeWithAgo() {
      return shortTimestamp(this.lastActivityAtTimeAgo, true);
    },
    waitingSinceTime() {
      return this.waitingSinceTimeAgo
        ? shortTimestamp(this.waitingSinceTimeAgo)
        : '';
    },
    displayText() {
      return this.waitingSinceTime
        ? 'Ждёт ' + this.waitingSinceTime
        : this.lastActivityTimeWithAgo;
    },
    createdAt() {
      if (!this.createdAtUnixTimestamp) return '';
      const createdTimeDiff = Date.now() - this.createdAtUnixTimestamp * 1000;
      const isBeforeAMonth = createdTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isBeforeAMonth
        ? this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.LATEST') +
            ' ' +
            this.createdAtTimeAgo
        : this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.OLDEST') +
            ' ' +
            dateFormat(this.createdAtUnixTimestamp);
    },
    lastActivity() {
      if (!this.lastActivityUnixTimestamp) return '';
      const lastActivityTimeDiff =
        Date.now() - this.lastActivityUnixTimestamp * 1000;
      const isNotActive = lastActivityTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isNotActive
        ? this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.ACTIVE') +
            ' ' +
            this.lastActivityAtTimeAgo
        : this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.NOT_ACTIVE') +
            ' ' +
            dateFormat(this.lastActivityUnixTimestamp);
    },
    waitingSince() {
      return this.waitingSinceTime
        ? 'Пациент ждёт ответа ' + this.waitingSinceTime
        : '';
    },
    tooltipText() {
      return [this.createdAt, this.lastActivity, this.waitingSince]
        .filter(Boolean)
        .join('\n');
    },
  },
  watch: {
    lastActivityTimestamp() {
      this.lastActivityAtTimeAgo = safeDynamicTime(this.lastActivityTimestamp);
    },
    createdAtTimestamp() {
      this.createdAtTimeAgo = safeDynamicTime(this.createdAtTimestamp);
    },
    waitingSinceTimestamp() {
      this.waitingSinceTimeAgo = safeDynamicTime(this.waitingSinceTimestamp);
    },
    conversationId() {
      // Reset display values and timer when the row is recycled to a different conversation.
      this.lastActivityAtTimeAgo = safeDynamicTime(this.lastActivityTimestamp);
      this.createdAtTimeAgo = safeDynamicTime(this.createdAtTimestamp);
      this.waitingSinceTimeAgo = safeDynamicTime(this.waitingSinceTimestamp);
      if (this.isAutoRefreshEnabled) {
        clearTimeout(this.timer);
        this.createTimer();
      }
    },
  },
  mounted() {
    if (this.isAutoRefreshEnabled) {
      this.createTimer();
    }
  },
  unmounted() {
    clearTimeout(this.timer);
  },
  methods: {
    createTimer() {
      this.timer = setTimeout(() => {
        this.lastActivityAtTimeAgo = safeDynamicTime(this.lastActivityTimestamp);
        this.createdAtTimeAgo = safeDynamicTime(this.createdAtTimestamp);
        this.waitingSinceTimeAgo = safeDynamicTime(this.waitingSinceTimestamp);
        this.createTimer();
      }, this.refreshTime());
    },
    refreshTime() {
      const referenceTimestamp =
        this.waitingSinceUnixTimestamp || this.lastActivityUnixTimestamp;
      if (!referenceTimestamp) return MINUTE_IN_MILLI_SECONDS;

      const timeDiff = Date.now() - referenceTimestamp * 1000;
      if (timeDiff > DAY_IN_MILLI_SECONDS) {
        return DAY_IN_MILLI_SECONDS;
      }
      if (timeDiff > HOUR_IN_MILLI_SECONDS) {
        return HOUR_IN_MILLI_SECONDS;
      }

      return MINUTE_IN_MILLI_SECONDS;
    },
  },
};
</script>

<template>
  <div
    v-tooltip.top="{
      content: tooltipText,
      delay: { show: 1000, hide: 0 },
    }"
    class="ml-auto leading-4 text-xxs text-n-slate-10 hover:text-n-slate-11"
  >
    <span class="whitespace-nowrap">{{ displayText }}</span>
  </div>
</template>
